// ============================================================
// Summit League v2 — Halo Infinite 4v4 Rules & Rotation
// Route: /rules (game-scoped via subdomain)
// Located: src/pages/GameRules/HaloInfinite4v4Rules.tsx
//
// Displays official gametypes, map rotation, match rules,
// dispute flow, and communication guidelines for Halo Infinite
// 4v4 Ranked play.
// ============================================================

import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { useAuthContext } from '../../context/AuthContext';

import '../../styles/user-profile.css';
import '../../styles/game-rules.css';

// ── Constants ───────────────────────────────────────────────

const DISCORD_INVITE_URL = 'https://discord.gg/zh9U2Cjw9R';

// ── Types ───────────────────────────────────────────────────

interface MapRotationGroup {
  title: string;
  subtitle: string;
  maps: string[];
}

// ── Component ───────────────────────────────────────────────

const HaloInfinite4v4Rules: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const userId = user?.userId;

  // ── Scroll to top on mount ───────────────────────────────

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  // ── Map Rotation Data ───────────────────────────────────

  const rotation: MapRotationGroup[] = useMemo(
    () => [
      {
        title: 'Ranked: Slayer',
        subtitle: 'Map Rotation',
        maps: ['Live Fire', 'Origin', 'Recharge', 'Solitude', 'Streets', 'Vacancy'],
      },
      {
        title: 'Ranked: CTF — 3 Capture',
        subtitle: 'Map Rotation',
        maps: ['Empyrean', 'Origin'],
      },
      {
        title: 'Ranked: CTF — 5 Capture',
        subtitle: 'Map Rotation',
        maps: ['Aquarius'],
      },
      {
        title: 'Ranked: Oddball',
        subtitle: 'Map Rotation',
        maps: ['Lattice', 'Live Fire', 'Recharge'],
      },
      {
        title: 'Ranked: King of the Hill',
        subtitle: 'Map Rotation',
        maps: ['Live Fire', 'Vacancy'],
      },
      {
        title: 'Ranked: Strongholds',
        subtitle: 'Map Rotation',
        maps: ['Live Fire', 'Recharge'],
      },
    ],
    []
  );

  // ── Handlers ────────────────────────────────────────────

  const handleUpdateDiscordTag = (): void => {
    if (!userId) {
      navigate('/auth');
      return;
    }
    navigate(`/profile/${userId}`);
  };

  // ── Render ──────────────────────────────────────────────

  return (
    <div className="sl-profile-page sl-rules-page">
      {/* ── Hero Header ────────────────────────────────── */}
      <div className="sl-profile-card sl-rules-hero">
        <div className="sl-rules-hero-top">
          <div className="sl-rules-hero-left">
            <div className="sl-rules-kicker">Halo Infinite</div>
            <div className="sl-rules-title">
              Gametypes, Rules &amp; Map Rotation
            </div>
            <div className="sl-rules-subtitle">
              Official Summit League rotation for Ranked play and match
              procedures. Please review before competing.
            </div>
          </div>

          <div className="sl-rules-hero-right">
            <div className="sl-rules-meta-card">
              <div className="sl-rules-meta-title">Quick Notes</div>

              <div className="sl-rules-meta-chips">
                <span className="sl-rules-chip sl-rules-chip--blue">
                  Best of 3 / Best of 5 / Best of 7
                </span>
                <span className="sl-rules-chip sl-rules-chip--amber">
                  Format is set when the match is created
                </span>
              </div>

              <div className="sl-rules-meta-text">
                Match communication is currently Discord-first. Update your
                Discord tag on your profile so teams and moderators can reach
                you.
              </div>

              <a
                className="sl-rules-discord-link"
                href={DISCORD_INVITE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Join Summit League Discord →
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Map Rotation Grid ──────────────────────────── */}
      <div className="sl-profile-section">
        <div className="sl-profile-section-title sl-rules-section-title">
          Gametypes, Rules &amp; Maps
        </div>

        <div className="sl-rules-grid">
          {rotation.map((group) => (
            <div
              key={group.title}
              className="sl-profile-card sl-rules-rotation-card"
            >
              <div className="sl-rules-rotation-head">
                <div className="sl-rules-rotation-title">{group.title}</div>
                <div className="sl-rules-rotation-subtitle">
                  {group.subtitle}
                </div>
              </div>

              <ul className="sl-rules-maplist">
                {group.maps.map((m) => (
                  <li
                    key={`${group.title}-${m}`}
                    className="sl-rules-mapitem"
                  >
                    <span className="sl-rules-dot" />
                    <span className="sl-rules-mapname">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Match Rules ────────────────────────────────── */}
      <div className="sl-profile-section">
        <div className="sl-profile-section-title sl-rules-section-title">
          Summit League — Match Rules
        </div>

        <div className="sl-rules-two-col">
          <div className="sl-profile-card sl-rules-rule-card">
            <div className="sl-profile-card-title">🏁 Match Format</div>
            <ul className="sl-rules-bullets">
              <li>
                Matches support Best of 3, Best of 5, and Best of 7 formats
              </li>
              <li>
                The match creator selects the format — the series ends once a
                team clinches the majority of games
              </li>
              <li>
                Gametypes and maps follow the official Summit League rotation
              </li>
            </ul>
          </div>

          <div className="sl-profile-card sl-rules-rule-card">
            <div className="sl-profile-card-title">
              ⏱️ Match Start &amp; Scheduling
            </div>
            <ul className="sl-rules-bullets">
              <li>Teams must be ready at the scheduled start time</li>
              <li>
                Failure to appear may result in a forfeit (moderator discretion)
              </li>
              <li>
                Matches may auto-expire if not accepted or started on time
              </li>
            </ul>
          </div>

          <div className="sl-profile-card sl-rules-rule-card">
            <div className="sl-profile-card-title">🧾 Reporting Results</div>
            <ul className="sl-rules-bullets">
              <li>Only team captains may submit match results</li>
              <li>Both teams must confirm the reported outcome</li>
              <li>Once confirmed, results are locked and final</li>
            </ul>
          </div>

          <div className="sl-profile-card sl-rules-rule-card">
            <div className="sl-profile-card-title">
              ⚠️ Disputes &amp; Contested Matches
            </div>
            <ul className="sl-rules-bullets">
              <li>
                Conflicting reports place the match in a Contested state
              </li>
              <li>
                Contested matches are reviewed by Summit League moderators
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Dispute Flow ───────────────────────────────── */}
      <div className="sl-profile-section">
        <div className="sl-profile-card sl-rules-callout sl-rules-callout--amber">
          <div className="sl-rules-callout-title">🧭 How Disputes Work</div>
          <div className="sl-rules-callout-text">
            If a match becomes contested or an issue arises, the match is
            flagged as{' '}
            <span className="sl-rules-inline-chip">Contested</span>. A team
            captain must post in{' '}
            <span className="sl-rules-inline-code">#moderator-request</span>{' '}
            and include:
          </div>

          <ul className="sl-rules-checklist">
            <li>Match ID</li>
            <li>Team names</li>
            <li>Brief explanation of the issue</li>
            <li>Screenshots or VODs (if available)</li>
          </ul>

          <div className="sl-rules-callout-foot">
            A moderator will review and make a ruling. Please don't argue in
            public channels or DMs — dispute handling stays in{' '}
            <span className="sl-rules-inline-code">#moderator-request</span>.
          </div>
        </div>
      </div>

      {/* ── Communication ──────────────────────────────── */}
      <div className="sl-profile-section">
        <div className="sl-profile-card sl-rules-callout sl-rules-callout--blue">
          <div className="sl-rules-callout-title">
            🎙️ Communication (Important)
          </div>
          <div className="sl-rules-callout-text">
            Summit League is a grassroots platform and currently relies on
            Discord for match communication.
          </div>

          <ul className="sl-rules-bullets sl-rules-bullets--tight">
            <li>Joining the Summit League Discord is strongly recommended</li>
            <li>
              Players should update their Discord tag in their user profile
            </li>
            <li>
              Discord may be used to resolve lobby issues or contact teams
            </li>
          </ul>

          <div className="sl-rules-callout-actions">
            <a
              className="sl-rules-discord-btn"
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Join Discord
            </a>

            <Button variant="ghost" onClick={handleUpdateDiscordTag}>
              Update Discord Tag
            </Button>
          </div>
        </div>
      </div>

      {/* ── Sportsmanship + Moderator Discretion ───────── */}
      <div className="sl-profile-section">
        <div className="sl-rules-two-col">
          <div className="sl-profile-card sl-rules-rule-card">
            <div className="sl-profile-card-title">🧠 Sportsmanship</div>
            <ul className="sl-rules-bullets">
              <li>
                Unsportsmanlike conduct may result in warnings or removal
              </li>
              <li>
                Exploits, intentional disconnects, or rule abuse are prohibited
              </li>
              <li>
                Respect opponents, admins, and the competitive environment
              </li>
            </ul>
          </div>

          <div className="sl-profile-card sl-rules-rule-card">
            <div className="sl-profile-card-title">
              🛡️ Moderator Discretion
            </div>
            <ul className="sl-rules-bullets">
              <li>All rules are subject to moderator interpretation</li>
              <li>Moderators reserve final decisions in edge cases</li>
              <li>
                Rulings prioritize competitive integrity and fairness
              </li>
            </ul>
          </div>
        </div>

        <div className="sl-rules-footnote">
          <span className="sl-rules-footnote-title">📌 Final Note:</span>{' '}
          Rules may evolve as the platform grows. Updates will be announced in
          Discord.
        </div>
      </div>
    </div>
  );
};

export default HaloInfinite4v4Rules;